import { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  untrackedCrestComplete,
  weeklyCrestCap,
  weeklyCrestEarned,
} from 'app/frontend/base/helpers/crestCap'
import type {
  UncappedCrestSource,
  UntrackedUncappedCrestSource,
} from 'app/frontend/types/custom/Season'
import { cn } from '@/lib/utils'
import ItemIcon from 'app/frontend/base/components/ItemIcon'

type Props = {
  crest: {
    first_period: number
    weekly_increase: number
    uncapped_crests?: Record<string, UncappedCrestSource>
    untracked_uncapped_crest?: UntrackedUncappedCrestSource
  }
  period: number
  earned?: number
  earnedSources?: Record<string, number>
  trackName?: string
  className?: string
  children: ReactNode
}

type SourceRowProps = {
  source: UncappedCrestSource
  url?: string
  complete: boolean | null
  earnedLabel?: ReactNode
}

const SourceRow = ({ source, url, complete, earnedLabel }: SourceRowProps) => {
  const content = (
    <>
      {source.icon ? <ItemIcon name={source.icon} className='h-4 w-4' /> : null}
      <span className='flex-1'>{source.name}</span>
      <span
        className={cn(
          'tabular-nums',
          complete === null
            ? 'text-foreground'
            : complete
            ? 'text-success-foreground'
            : 'text-warning-foreground',
        )}
      >
        {earnedLabel === undefined ? null : <>{earnedLabel} / </>}
        {source.amount}
      </span>
    </>
  )

  return (
    <li>
      {url ? (
        <a
          href={`https://wowhead.com${url}`}
          target='_blank'
          rel='noreferrer'
          className='flex items-center gap-1.5'
        >
          {content}
        </a>
      ) : (
        <div className='flex items-center gap-1.5'>{content}</div>
      )}
    </li>
  )
}

const CrestCapTooltip = ({
  crest,
  period,
  earned,
  earnedSources = {},
  trackName,
  className,
  children,
}: Props) => {
  const showProgress = earned !== undefined
  const untracked = crest.untracked_uncapped_crest
  const untrackedComplete =
    showProgress && untrackedCrestComplete(crest, period, earned, earnedSources)
  const weeklyCap = weeklyCrestCap(crest, period)
  const weeklyEarned = showProgress
    ? weeklyCrestEarned(
        earned,
        weeklyCap,
        earnedSources,
        untrackedComplete && untracked ? untracked.amount : 0,
      )
    : 0
  const sources = Object.entries(crest.uncapped_crests ?? {})

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>{children}</div>
      </TooltipTrigger>
      <TooltipContent className='min-w-48 px-3 py-2.5'>
        <div className='text-center'>
          {trackName ? (
            <div className='text-xs font-semibold'>{trackName}</div>
          ) : null}
          <div className='text-base font-semibold tabular-nums'>
            {showProgress ? (
              <>
                <span
                  className={
                    weeklyEarned === weeklyCap
                      ? 'text-success-foreground'
                      : 'text-warning-foreground'
                  }
                >
                  {weeklyEarned}
                </span>
                {' / '}
              </>
            ) : null}
            {weeklyCap}
          </div>
        </div>

        {sources.length > 0 || untracked ? (
          <div className='mt-2 border-t pt-2'>
            <div className='mb-1.5 text-[11px] text-muted-foreground'>
              Uncapped sources
            </div>
            <ul className='space-y-1'>
              {sources.map(([url, source]) => {
                const complete = !!earnedSources[url]

                return (
                  <SourceRow
                    key={url}
                    source={source}
                    url={url}
                    complete={showProgress ? complete : null}
                    earnedLabel={
                      showProgress ? (complete ? source.amount : 0) : undefined
                    }
                  />
                )
              })}
              {untracked ? (
                <SourceRow
                  source={untracked}
                  url={untracked.url}
                  complete={untrackedComplete || null}
                  earnedLabel={
                    showProgress
                      ? untrackedComplete
                        ? untracked.amount
                        : '?'
                      : undefined
                  }
                />
              ) : null}
            </ul>
          </div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}

export default CrestCapTooltip
